import { WORKFLOW_FIRST_PATH } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { Button, DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger, Icon } from "@sagot/ui";
import { useRef, useState } from "react";
import { useBuilderTools } from "./builder-tools";
import { PathDeleteDialog } from "./path-delete-dialog";

const P = "engage.workflows.extras.paths";

interface PathActionsProps {
  id: string;
  /** "Path C · Tuition": names the menu and the confirmation. */
  title: string;
  /** Rename: the header swaps its title for the name field. */
  onRename: () => void;
}

/** The ⋯ menu of a path, on its card in the list and on its tile in the canvas: Rename, Duplicate and (not for Path A) Delete. */
export function PathActions({ id, title, onRename }: PathActionsProps) {
  const { t } = useTranslation();
  const tools = useBuilderTools();
  const [confirming, setConfirming] = useState(false);
  const trigger = useRef<HTMLButtonElement>(null);
  // What a menu item does once the menu has closed and given up its hold on focus: the name field and the new path need focus for themselves.
  const afterClose = useRef<(() => void) | null>(null);
  if (!tools?.editable) return null;
  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button ref={trigger} type="button" variant="ghost" size="sm" iconOnly aria-label={t(`${P}.actions`, { path: title })} data-testid={`path-actions-${id}`}>
            <Icon name="more-horizontal" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="end"
          className="w-48"
          onCloseAutoFocus={(event) => {
            const action = afterClose.current;
            if (!action) return;
            afterClose.current = null;
            event.preventDefault();
            action();
          }}
        >
          <DropdownMenuItem onSelect={() => { afterClose.current = onRename; }}>
            <Icon name="pencil" />
            {t(`${P}.rename`)}
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={() => { afterClose.current = () => { tools.duplicate(id); }; }}>
            <Icon name="copy" />
            {t(`${P}.duplicate`)}
          </DropdownMenuItem>
          {id === WORKFLOW_FIRST_PATH ? null : (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem tone="danger" onSelect={() => { setConfirming(true); }}>
                <Icon name="trash" />
                {t(`${P}.delete`)}
              </DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
      <PathDeleteDialog
        open={confirming}
        onOpenChange={setConfirming}
        pathTitle={title}
        connections={tools.incomingCount(id)}
        notes={tools.notesAt(id).length}
        onConfirm={() => { tools.remove(id); }}
        onCloseAutoFocus={(event) => {
          event.preventDefault();
          trigger.current?.focus();
        }}
      />
    </>
  );
}
